import type { SeatReservation } from 'colyseus.js';
import type { AuthUser } from '../core/state/auth-store';
import { connectSocket, disconnectSocket } from '../core/network/socket';
import { api } from '../core/network/api';
import { authStore } from '../core/state/auth-store';
import { mountSconceHub } from './sconce-hub-bootstrap';
import { mountDungeonInstance } from './dungeon-instance-bootstrap';
import { mountArena } from './arena-bootstrap';
import { adoptLocaleSetting, localePersisted, RELANGUAGE_EVENT } from './i18n/i18n';

/**
 * design/gdd/sconce-hub.md Core Rule #8 — the hub gateway (Socket.io) and a
 * match (Colyseus) are architecturally independent connections; the
 * frontend is responsible for disconnecting from one when entering the
 * other and reconnecting on return. This is the `/play` default: it owns
 * that one state machine (hub <-> match) so neither scene needs to know
 * about the other's mounting details.
 */
/**
 * Reconnects the hub socket on the way back from a match, with a live access
 * cookie (2026-10-15, QA 10.10 #13: "return to lobby sometimes logs me out").
 *
 * The race: the socket handshake authenticates with the `gamer_id` access
 * cookie alone (15-minute maxAge), and a match talks only to Colyseus, so
 * nothing refreshes that cookie while the player is in it. Return after it
 * has lapsed and the old `connectSocket()` handshook with no cookie: the
 * gateway answers `server:error AUTH_FAILED`, core `socket.ts` sets the user
 * to null, and main.ts's authStore subscriber routes /play to /login —
 * "Welcome back" — even though the 3-day refresh cookie was fine. "Sometimes"
 * is whether the hub's own first API call (which 401s, refreshes and sets a
 * new cookie) happened to land before the handshake.
 *
 * So the socket waits for one authenticated request first. `/auth/me` goes
 * through the shared axios interceptor, so an expired cookie is refreshed
 * there (deduplicated with the hub's own calls); only a dead refresh token
 * ends the session, and that is a real sign-out.
 */
export async function reconnectHubSocket(): Promise<void> {
  try { await api.get('/auth/me'); } catch { /* a dead session is handled by the interceptor */ }
  if (authStore.get().user) connectSocket();
}

export function mountSession(root: HTMLElement, user: AuthUser, onLogout: () => void): () => void {
  let active = true;
  let innerTeardown: (() => void) | null = null;
  let inHub = false;

  function showHub(autoQueue = false): void {
    inHub = true;
    innerTeardown = mountSconceHub(root, user, onLogout, (reservation) => {
      inHub = false;
      innerTeardown?.();
      disconnectSocket();
      showMatch(reservation as SeatReservation);
    }, autoQueue);
  }

  function showMatch(reservation: SeatReservation): void {
    /**
     * `requeue` comes from the run-end screen's "Queue Again" button — the
     * player is returning to the hub either way, and the flag only decides
     * whether it starts searching the moment it lands. Routing it through the
     * hub rather than queuing from the match scene keeps one owner of the
     * hub<->match state machine, which is this file's whole job.
     */
    const onEnd = (requeue = false) => {
      if (!active) return;
      // The hub mounts at once; socket emits (an auto-requeue) are buffered
      // by socket.io until the reconnect below completes.
      showHub(requeue);
      void reconnectHubSocket();
    };
    // design/gdd/pvp-arena.md — a genuinely separate Colyseus room type from
    // dungeon_instance; the reservation's own room name says which one this is.
    innerTeardown = reservation.room.name === 'pvp_arena'
      ? mountArena(root, user, onLogout, reservation, onEnd)
      : mountDungeonInstance(root, user, onLogout, reservation, onEnd);
  }

  /**
   * The language changed and could not be saved for the next boot (storage
   * blocked): redraw the hub in it rather than reload, which would come back
   * in the browser language with half the screens in the account's. Not
   * mid-match — the match's screens pick the catalogue up as they draw, and
   * the hub is redrawn on the way back anyway. Never reloads, so never loops.
   */
  const relanguage = (): void => {
    if (!active || !inHub) return;
    innerTeardown?.();
    showHub();
  };
  window.addEventListener(RELANGUAGE_EVENT, relanguage);

  showHub();
  void syncAccountLanguage(() => active);

  return () => {
    window.removeEventListener(RELANGUAGE_EVENT, relanguage);
    active = false;
    innerTeardown?.();
    innerTeardown = null;
  };
}

/**
 * The account's language lives on the server; this browser boots from its
 * own copy (engine/i18n). A first sign-in on a new device, or a change made
 * on another one, lands here: adopt it and redraw once (2026-10-16).
 */
async function syncAccountLanguage(stillMounted: () => boolean): Promise<void> {
  try {
    const overview = await api.get<{ profile?: { locale?: string | null } }>('/settings');
    const setting = overview?.profile?.locale ?? null;
    if (await adoptLocaleSetting(setting) && stillMounted()) {
      // Reload only when the choice survives it — with storage blocked the
      // next boot would pick the browser language again and reload forever,
      // so redraw in memory instead.
      if (localePersisted(setting)) location.reload();
      else window.dispatchEvent(new Event(RELANGUAGE_EVENT));
    }
  } catch {
    // Offline or a server hiccup: the language this browser already has stands.
  }
}
